import type { ComponentPropsWithRef, ElementType, FunctionComponent, ReactElement } from 'react';

import type { SideNavigationShortcut } from './SideNavigationTooltip';
import { SideNavigationTooltip } from './SideNavigationTooltip';
import { navRowClassName, navRowIconClassName, navRowLabelClassName } from './side-navigation-classes';

type SideNavigationRowOwnProps<E extends ElementType> = {
    readonly active?: boolean;
    readonly as?: E;
    readonly expanded: boolean;
    readonly Icon: FunctionComponent<{ readonly className?: string }>;
    readonly IconAdornment?: FunctionComponent;
    readonly label: string;
    // Its `G then key` chord, shown in the row's hint.
    readonly shortcut?: SideNavigationShortcut;
};

export type SideNavigationRowProps<E extends ElementType = 'button'> = SideNavigationRowOwnProps<E> &
    Omit<ComponentPropsWithRef<E>, keyof SideNavigationRowOwnProps<E> | 'children' | 'className'>;

// One top-level entry: icon and label. Render it as the app's router link through `as`.
export const SideNavigationRow = <E extends ElementType = 'button'>({
    active = false,
    as,
    expanded,
    Icon,
    IconAdornment,
    label,
    shortcut,
    ...props
}: SideNavigationRowProps<E>): ReactElement => {
    const Component = as ?? 'button';

    const row = (
        <Component
            aria-current={active ? 'page' : undefined}
            aria-label={label}
            className={navRowClassName(active)}
            {...(as === undefined ? { type: 'button' } : {})}
            {...props}
        >
            <span aria-hidden={true} className={navRowIconClassName(active)}>
                <Icon className="size-[18px]" />
                {IconAdornment ? <IconAdornment /> : null}
            </span>
            <span className={navRowLabelClassName(expanded)}>{label}</span>
        </Component>
    );

    return (
        <SideNavigationTooltip expanded={expanded} label={label} shortcut={shortcut}>
            {row}
        </SideNavigationTooltip>
    );
};
